/**
 * Mesh factory for command outputs.
 *
 * Primitives, extruded profiles, surfaces and loft/sweep results are all
 * produced as plain serializable mesh payloads ({vertices, indices}) so the
 * shared EntityLayer can render every geometry-producing command through a
 * single 'mesh' branch, and the A2 ledger/CadReferenceLayer keep working.
 * Z-up world convention (A5): height/extrude axis is +Z.
 */

import type { Pt3 } from './geometryMath';
import { polylineLength } from './geometryMath';

export interface MeshPayload {
    vertices: number[][];
    indices: number[][];
}

function pushQuad(indices: number[][], a: number, b: number, c: number, d: number) {
    indices.push([a, b, c], [a, c, d]);
}

/** Axis-angle rotate a vertex (axis unit vector). */
function rot(p: Pt3, axis: [number, number, number], ang: number): Pt3 {
    const [ux, uy, uz] = axis;
    const c = Math.cos(ang), s = Math.sin(ang), t = 1 - c;
    const [x, y, z] = p;
    return [
        (t * ux * ux + c) * x + (t * ux * uy - s * uz) * y + (t * ux * uz + s * uy) * z,
        (t * ux * uy + s * uz) * x + (t * uy * uy + c) * y + (t * uy * uz - s * ux) * z,
        (t * ux * uz - s * uy) * x + (t * uy * uz + s * ux) * y + (t * uz * uz + c) * z,
    ];
}

export function boxMesh(w: number, d: number, h: number): MeshPayload {
    const vertices: number[][] = [
        [-w / 2, -d / 2, 0], [w / 2, -d / 2, 0], [w / 2, d / 2, 0], [-w / 2, d / 2, 0],
        [-w / 2, -d / 2, h], [w / 2, -d / 2, h], [w / 2, d / 2, h], [-w / 2, d / 2, h],
    ];
    const indices = [
        [0, 3, 2], [0, 2, 1],      // bottom
        [4, 5, 6], [4, 6, 7],      // top
        [0, 1, 5], [0, 5, 4],      // -y
        [2, 3, 7], [2, 7, 6],      // +y
        [1, 2, 6], [1, 6, 5],      // +x
        [3, 0, 4], [3, 4, 7],      // -x
    ];
    return { vertices, indices };
}

export function cylinderMesh(r: number, h: number, segments = 32, rTop?: number): MeshPayload {
    const rt = rTop ?? r;
    const vertices: number[][] = [];
    const indices: number[][] = [];
    const base = vertices.length;
    vertices.push([0, 0, 0]);
    for (let i = 0; i < segments; i++) {
        const a = (i / segments) * Math.PI * 2;
        vertices.push([Math.cos(a) * r, Math.sin(a) * r, 0]);
    }
    const topCenter = vertices.length;
    vertices.push([0, 0, h]);
    const topStart = vertices.length;
    for (let i = 0; i < segments; i++) {
        const a = (i / segments) * Math.PI * 2;
        vertices.push([Math.cos(a) * rt, Math.sin(a) * rt, h]);
    }
    for (let i = 0; i < segments; i++) {
        const next = (i + 1) % segments;
        indices.push([base, base + 1 + next, base + 1 + i]);
        indices.push([topCenter, topStart + i, topStart + next]);
        const b0 = base + 1 + i, b1 = base + 1 + next;
        pushQuad(indices, b0, b1, topStart + next, topStart + i);
    }
    return { vertices, indices };
}

export const coneMesh = (r: number, h: number, seg = 32) => cylinderMesh(r, h, seg, 0.001);
export const truncatedConeMesh = (r: number, h: number, rt: number, seg = 32) => cylinderMesh(r, h, seg, rt);

export function sphereMesh(r: number, segW = 24, segH = 16): MeshPayload {
    const vertices: number[][] = [];
    const indices: number[][] = [];
    for (let j = 0; j <= segH; j++) {
        const phi = (j / segH) * Math.PI;
        for (let i = 0; i <= segW; i++) {
            const theta = (i / segW) * Math.PI * 2;
            vertices.push([
                r * Math.sin(phi) * Math.cos(theta),
                r * Math.sin(phi) * Math.sin(theta),
                r * Math.cos(phi),
            ]);
        }
    }
    const row = segW + 1;
    for (let j = 0; j < segH; j++) {
        for (let i = 0; i < segW; i++) {
            const a = j * row + i, b = a + 1, d = a + row, e = d + 1;
            indices.push([a, d, b], [b, d, e]);
        }
    }
    return { vertices, indices };
}

/** Ellipsoid from three radii (unit sphere scaled). */
export function ellipsoidMesh(rx: number, ry: number, rz: number): MeshPayload {
    const m = sphereMesh(1);
    return {
        vertices: m.vertices.map(([x, y, z]) => [x * rx, y * ry, z * rz]),
        indices: m.indices,
    };
}

export function pyramidMesh(r: number, h: number, sides = 4): MeshPayload {
    const vertices: number[][] = [[0, 0, 0], [0, 0, h]];
    for (let i = 0; i < sides; i++) {
        const a = (i / sides) * Math.PI * 2 - Math.PI / 2;
        vertices.push([Math.cos(a) * r, Math.sin(a) * r, 0]);
    }
    const indices: number[][] = [];
    for (let i = 0; i < sides; i++) {
        const next = (i + 1) % sides;
        indices.push([0, 2 + next, 2 + i]);
        indices.push([1, 2 + i, 2 + next]);
    }
    return { vertices, indices };
}

export function torusMesh(R: number, tube: number, segU = 32, segV = 16): MeshPayload {
    const vertices: number[][] = [];
    const indices: number[][] = [];
    for (let j = 0; j <= segU; j++) {
        const u = (j / segU) * Math.PI * 2;
        for (let i = 0; i <= segV; i++) {
            const v = (i / segV) * Math.PI * 2;
            const x = (R + tube * Math.cos(v)) * Math.cos(u);
            const y = (R + tube * Math.cos(v)) * Math.sin(u);
            const z = tube * Math.sin(v);
            vertices.push([x, y, z]);
        }
    }
    const row = segV + 1;
    for (let j = 0; j < segU; j++) {
        for (let i = 0; i < segV; i++) {
            const a = j * row + i, b = a + 1, d = a + row, e = d + 1;
            indices.push([a, d, b], [b, d, e]);
        }
    }
    return { vertices, indices };
}

/** Sweep a circle along a polyline path (PIPE). */
export function pipeMesh(path: Pt3[], rStart: number, rEnd: number, radialSeg = 12): MeshPayload {
    if (path.length < 2) return { vertices: [], indices: [] };
    const vertices: number[][] = [];
    const indices: number[][] = [];
    const rings: number[][] = [];
    const total = polylineLength(path);
    let walked = 0;
    for (let s = 0; s < path.length - 1; s++) {
        const pa = path[s], pb = path[s + 1];
        const dir = [pb[0] - pa[0], pb[1] - pa[1], pb[2] - pa[2]];
        const len = Math.hypot(dir[0], dir[1], dir[2]) || 1;
        dir[0] /= len; dir[1] /= len; dir[2] /= len;
        const tmp = Math.abs(dir[2]) < 0.9 ? [0, 0, 1] : [1, 0, 0];
        let u = [
            dir[1] * tmp[2] - dir[2] * tmp[1],
            dir[2] * tmp[0] - dir[0] * tmp[2],
            dir[0] * tmp[1] - dir[1] * tmp[0],
        ];
        const ul = Math.hypot(u[0], u[1], u[2]) || 1;
        u = u.map((x) => x / ul);
        const v = [
            dir[1] * u[2] - dir[2] * u[1],
            dir[2] * u[0] - dir[0] * u[2],
            dir[0] * u[1] - dir[1] * u[0],
        ];
        const steps = Math.max(1, Math.round((len / (total || 1)) * 16));
        for (let i = 0; i <= steps; i++) {
            const k = i / steps;
            if (i > 0 && s > 0) continue; // avoid duplicate ring on joint
            const cx = pa[0] + dir[0] * len * k;
            const cy = pa[1] + dir[1] * len * k;
            const cz = pa[2] + dir[2] * len * k;
            const along = (walked + len * k) / (total || 1);
            const r = rStart + (rEnd - rStart) * along;
            const ring: number[] = [];
            for (let q = 0; q < radialSeg; q++) {
                const ang = (q / radialSeg) * Math.PI * 2;
                const ca = Math.cos(ang) * r, sa = Math.sin(ang) * r;
                ring.push(vertices.length);
                vertices.push([
                    cx + u[0] * ca + v[0] * sa,
                    cy + u[1] * ca + v[1] * sa,
                    cz + u[2] * ca + v[2] * sa,
                ]);
            }
            rings.push(ring);
        }
        walked += len;
    }
    for (let s = 0; s < rings.length - 1; s++) {
        const a = rings[s], b = rings[s + 1];
        const n = Math.min(a.length, b.length);
        for (let i = 0; i < n; i++) {
            const j = (i + 1) % n;
            pushQuad(indices, a[i], a[j], b[j], b[i]);
        }
    }
    return { vertices, indices };
}

/** Grid (rows x cols) surface mesh from a point grid. */
export function gridMesh(grid: Pt3[][]): MeshPayload {
    const vertices: number[][] = [];
    const indices: number[][] = [];
    grid.forEach((row) => row.forEach((p) => vertices.push([p[0], p[1], p[2]])));
    const cols = grid[0]?.length ?? 0;
    for (let r = 0; r < grid.length - 1; r++) {
        for (let c = 0; c < cols - 1; c++) {
            const a = r * cols + c, b = a + 1, d = a + cols, e = d + 1;
            pushQuad(indices, a, b, e, d);
        }
    }
    return { vertices, indices };
}

/** Extrude a closed profile along a direction (EXTRUDESRF / PUSH/PULL of 2D). */
export function extrudeProfile(profile: Pt3[], dir: Pt3, cap = true): MeshPayload {
    const n = profile.length;
    if (n < 3) return { vertices: [], indices: [] };
    const vertices = [...profile.map((p) => [...p] as number[]), ...profile.map((p) => [p[0] + dir[0], p[1] + dir[1], p[2] + dir[2]])];
    const indices: number[][] = [];
    for (let i = 0; i < n; i++) {
        const j = (i + 1) % n;
        pushQuad(indices, i, j, n + j, n + i);
    }
    if (cap) {
        // fan caps using the vertex list order (convex-safe enough for
        // profiles produced by rectangle/circle/ellipse tools)
        for (let i = 1; i < n - 1; i++) indices.push([0, i, i + 1]);
        for (let i = 1; i < n - 1; i++) indices.push([n, 2 * n - 1 - i, n + i]);
    }
    return { vertices, indices };
}

/** Revolve a profile around an axis through two points (REVOLVE). */
export function revolveMesh(profile: Pt3[], axisA: Pt3, axisB: Pt3, angleDeg = 360, segments = 48): MeshPayload {
    const axis: [number, number, number] = (() => {
        const d = [axisB[0] - axisA[0], axisB[1] - axisA[1], axisB[2] - axisA[2]];
        const l = Math.hypot(d[0], d[1], d[2]) || 1;
        return [d[0] / l, d[1] / l, d[2] / l];
    })();
    const sweep = (angleDeg * Math.PI) / 180;
    const steps = Math.max(4, Math.round((sweep / (Math.PI * 2)) * segments));
    const vertices: number[][] = [];
    const indices: number[][] = [];
    for (let s = 0; s <= steps; s++) {
        const ang = (s / steps) * sweep;
        for (const p of profile) {
            const translated: Pt3 = [p[0] - axisA[0], p[1] - axisA[1], p[2] - axisA[2]];
            const r = rot(translated, axis, ang);
            vertices.push([r[0] + axisA[0], r[1] + axisA[1], r[2] + axisA[2]]);
        }
    }
    const n = profile.length;
    for (let s = 0; s < steps; s++) {
        for (let i = 0; i < n - 1; i++) {
            const a = s * n + i, b = a + 1, d = a + n, e = d + 1;
            pushQuad(indices, a, b, e, d);
        }
    }
    return { vertices, indices };
}

/** Loft between equal-length sections (LOFT/SWEEP-style skin). */
export function loftMesh(sections: Pt3[][]): MeshPayload {
    if (sections.length < 2) return { vertices: [], indices: [] };
    const cols = Math.min(...sections.map((s) => s.length));
    if (cols < 2) return { vertices: [], indices: [] };
    const vertices: number[][] = [];
    const indices: number[][] = [];
    sections.forEach((sec) => { for (let i = 0; i < cols; i++) { const p = sec[i]; vertices.push([p[0], p[1], p[2]]); } });
    for (let s = 0; s < sections.length - 1; s++) {
        for (let i = 0; i < cols - 1; i++) {
            const a = s * cols + i, b = a + 1, d = a + cols, e = d + 1;
            pushQuad(indices, a, b, e, d);
        }
    }
    return { vertices, indices };
}

/** Offset a mesh along its vertex normals (OFFSETSrf/thicken-lite). */
export function offsetMesh(mesh: MeshPayload, distance: number): MeshPayload {
    const normals = vertexNormals(mesh);
    return {
        vertices: mesh.vertices.map((v, i) => [v[0] + normals[i][0] * distance, v[1] + normals[i][1] * distance, v[2] + normals[i][2] * distance]),
        indices: mesh.indices,
    };
}

export function vertexNormals(mesh: MeshPayload): number[][] {
    const normals = mesh.vertices.map(() => [0, 0, 0]);
    for (const [a, b, c] of mesh.indices) {
        const va = mesh.vertices[a], vb = mesh.vertices[b], vc = mesh.vertices[c];
        const e1 = [vb[0] - va[0], vb[1] - va[1], vb[2] - va[2]];
        const e2 = [vc[0] - va[0], vc[1] - va[1], vc[2] - va[2]];
        const cr = [e1[1] * e2[2] - e1[2] * e2[1], e1[2] * e2[0] - e1[0] * e2[2], e1[0] * e2[1] - e1[1] * e2[0]];
        [a, b, c].forEach((vi) => { normals[vi][0] += cr[0]; normals[vi][1] += cr[1]; normals[vi][2] += cr[2]; });
    }
    return normals.map((n) => {
        const l = Math.hypot(n[0], n[1], n[2]) || 1;
        return [n[0] / l, n[1] / l, n[2] / l];
    });
}
